import { X } from "lucide-react";

import { Badge, badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { isHcbbFilterFieldActive } from "@/lib/hcbb-cost-estimator-utils";
import type { HcbbAdvancedFilters } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";

/** Which committed fields become a chip, in the order they read left to right. */
const CHIP_FIELDS: { key: keyof HcbbAdvancedFilters; label: string }[] = [
  { key: "networkName", label: "Network" },
  { key: "facilityType", label: "Facility Type" },
  { key: "specialty", label: "Specialty" },
  { key: "physicianQuery", label: "Physician" },
  { key: "facilityQuery", label: "Facility" },
];

type HcbbActiveFiltersProps = {
  filters: HcbbAdvancedFilters;
  onRemove: (key: keyof HcbbAdvancedFilters) => void;
  onClearAll: () => void;
};

/**
 * The committed advanced-search criteria, one chip per field, beneath the
 * search form.
 *
 * Chips act on the committed filters directly rather than through the
 * panel's own draft/Apply flow — removing one is a smaller, more reversible
 * action than opening the panel, so it takes effect immediately, the same
 * way the filters were applied in the first place would be undone one field
 * at a time.
 */
export const HcbbActiveFilters = ({
  filters,
  onRemove,
  onClearAll,
}: HcbbActiveFiltersProps) => {
  // A facility/physician query below the three-letter search minimum is
  // still being typed — `isHcbbFilterFieldActive` is the single place that
  // knows this, shared with the filter toggle's own badge count, so a chip
  // never claims a field is applied that isn't actually narrowing anything.
  const active = CHIP_FIELDS.filter(({ key }) =>
    isHcbbFilterFieldActive(key, filters[key]),
  );

  if (active.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="Applied advanced search filters"
      className="flex flex-wrap items-center gap-2"
    >
      <span className="text-sm font-medium text-gray-700">
        Applied Filters:
      </span>

      {active.map(({ key, label }) => (
        <Badge
          key={key}
          variant="secondary"
          className="gap-1.5 rounded-full py-1 pl-3 pr-1.5 font-normal"
        >
          <span>
            {label}: {filters[key]}
          </span>
          <button
            type="button"
            onClick={() => onRemove(key)}
            aria-label={`Remove ${label} filter`}
            className="hover:bg-foreground/10 rounded-full p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </Badge>
      ))}

      {/* Text only, unlike the per-field chips above: this acts on every
          criterion at once rather than one, so it reads as a distinct,
          heavier action rather than another removable chip — an X here would
          visually equate the two. */}
      <button
        type="button"
        onClick={onClearAll}
        className={cn(
          badgeVariants({ variant: "destructive" }),
          "hover:bg-destructive/90 rounded-full px-3 py-1 font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        Clear All
      </button>
    </div>
  );
};
